import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import coffeeTalkBackground from '../images/coffee-talk.jpg';
import coffeeImage from '../images/coffee.jpg';

const PAGE_URL = 'https://createadish.com/dangers-of-coffee';
const PAGE_TITLE = 'Dangers of Coffee: Cafés, Free Speech & the Power of Conversation | Create A Dish';
const PAGE_DESCRIPTION =
  'Coffee feels harmless now — but for much of history, coffeehouses were feared by kings and sultans. Discover the surprising political history of coffee and why the café became a battleground for free speech.';

const Coffee: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="dangers of coffee, coffeehouse history, coffee and free speech, penny university, Ottoman coffeehouses, King Charles II coffeehouses, coffee history, café culture, history of conversation"
        />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />

        {/* Canonical */}
        <link rel="canonical" href={PAGE_URL} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Coffee, Cafés, and the Dangerous Power of Conversation',
            description: PAGE_DESCRIPTION,
            author: {
              '@type': 'Organization',
              name: 'Create A Dish',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Create A Dish',
              logo: {
                '@type': 'ImageObject',
                url: 'https://createadish.com/logo.png',
              },
            },
            datePublished: '2026-05-08',
            dateModified: '2026-05-08',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': PAGE_URL,
            },
          })}
        </script>
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '1200px',
          mx: 'auto',
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            height: { xs: '40vh', md: '50vh' },
            backgroundImage: `url(${coffeeTalkBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(1px)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 1,
              }}
            >
              Coffee, Cafés, and the
            </Typography>
            <Typography
              variant="h4"
              component="span"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              }}
            >
              Dangerous Power of Conversation
            </Typography>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{
              mb: 4,
              '& .MuiBreadcrumbs-separator': { color: 'primary.main' },
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/')}
              sx={{
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              Home
            </Link>
            <Typography
              sx={{ fontSize: '1rem', fontWeight: 600, color: 'primary.main' }}
            >
              Dangers of Coffee
            </Typography>
          </Breadcrumbs>

          {/* Intro */}
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Coffee feels harmless now. It is the morning ritual, the after-dinner comfort, the excuse
            to meet a friend, the fuel behind recipe testing, writing, studying, and long conversations
            at the kitchen table.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            But for much of history, coffee was not treated as harmless.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            The drink itself was only part of the story. What worried rulers, religious authorities, and
            political elites was the place coffee created: the coffeehouse. Wherever coffeehouses
            appeared, people gathered. They talked. They argued. They read news aloud. They passed
            rumors. They debated politics, religion, trade, literature, and power.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            A cup of coffee became a doorway into public conversation.
          </Typography>
          <Typography variant="body1" sx={{ mb: 6, lineHeight: 1.8, fontStyle: 'italic' }}>
            And public conversation has always made authorities nervous.
          </Typography>

          {/* Section 1 */}
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}
          >
            From Bean to Gathering Place
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Coffee's early history is usually traced through East Africa, Yemen, the Ottoman world, and
            then into Europe. By the 1500s and 1600s, coffeehouses were becoming important urban
            institutions. They were not just places to drink something hot and bitter. They were places
            where ordinary people could sit, listen, argue, and learn.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            In Ottoman coffeehouses, people from different social levels gathered for entertainment,
            news, gossip, and political talk. Coffeehouses helped spread information in societies where
            not everyone could read or afford printed material. Some historians have described them as
            informal centers of education and public opinion. They were also watched closely by
            authorities because they could become centers of political rumor, unrest, and resistance.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            That pattern repeated elsewhere.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            In England, coffeehouses became known as places where men gathered to read newspapers,
            discuss politics, exchange ideas, and debate the state of the nation. They were sometimes
            called "penny universities" because, for the price of a cup of coffee, a visitor could
            enter a world of conversation, argument, and information.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            That was exciting for citizens.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontStyle: 'italic' }}>
            It was troubling for kings.
          </Typography>

          {/* Inline coffee.jpg image */}
          <Box
            component="img"
            src={coffeeImage}
            alt="A steaming cup of coffee"
            sx={{
              display: 'block',
              width: '100%',
              maxWidth: 500,
              mx: 'auto',
              my: 4,
              borderRadius: 2,
              boxShadow: 3,
            }}
          />

          {/* Section 2 */}
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}
          >
            When Coffeehouses Became Too Talkative
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            In 1675, King Charles II of England issued a proclamation aimed at suppressing coffeehouses.
            The concern was not simply coffee. The concern was speech. The official complaint was that
            coffeehouses attracted "idle and disaffected persons" who spread "malicious and scandalous
            reports" about the government.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontStyle: 'italic' }}>
            In other words, people were talking too much.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            The attempt did not last, but the instinct behind it is familiar: control the meeting place,
            and you can control the conversation. Control the conversation, and you can control the
            spread of ideas.
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8 }}>
            Coffeehouses had become something more powerful than refreshment stands. They were social
            networks before social media. They were comment sections before the internet. They were
            podcast studios, news feeds, debate halls, rumor mills, and civic classrooms all at once.
          </Typography>
          <Typography variant="body1" sx={{ mb: 6, lineHeight: 1.8 }}>
            The same fear appeared in the Ottoman Empire. Sultan Murad IV targeted coffeehouses in
            Istanbul in the 1600s, seeing them as dangerous gathering places where dissent and rebellion
            could grow. Some accounts exaggerate the idea that coffee itself was universally banned, but
            the central point remains: authorities feared the coffeehouse as a political and social
            space.
          </Typography>
          <Typography variant="body1" sx={{ mb: 6, lineHeight: 1.8, fontWeight: 600 }}>
            It was not caffeine that threatened power. It was conversation.
          </Typography>

          {/* Section 3 */}
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}
          >
            The Café as a Recipe for Ideas
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            A café is built from simple ingredients: coffee, tables, time, and people.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontStyle: 'italic' }}>
            But those ingredients can produce something powerful.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            One person brings a newspaper. Another brings a complaint. Someone else brings a joke, a
            rumor, a poem, a political opinion, a business idea, or a question that should not be asked
            too loudly. Soon the room is no longer just a room. It becomes a public square.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            That is why coffee and free speech belong in the same story.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Food and drink have always shaped human conversation. Families gather around kitchen tables.
            Friends linger over dessert. Strangers become regulars at the same counter. Recipes move
            from person to person because people talk while they cook, serve, eat, and drink.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Coffeehouses made that process public.
          </Typography>
          <Typography variant="body1" sx={{ mb: 6, lineHeight: 1.8 }}>
            They turned private thoughts into shared discussion.
          </Typography>

          {/* Section 4 */}
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}
          >
            Why This Still Matters
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Today, the café has many forms. It may be a neighborhood coffee shop, a diner booth, a
            restaurant table, a group chat, a podcast, a social media thread, or an AI-powered website.
            The setting changes, but the basic question remains the same:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontStyle: 'italic' }}>
            Are people free to gather, speak, disagree, and test ideas?
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            Every society has to decide how much conversation it can tolerate. Some speech is false.
            Some is rude. Some is foolish. Some is dangerous. But history shows that authorities often
            begin by saying they only want to stop disorder, rumor, or harmful ideas — and end by
            restricting the ordinary spaces where people think out loud.
          </Typography>
          <Typography variant="body1" sx={{ mb: 6, lineHeight: 1.8 }}>
            That is why the history of coffeehouses is also a history of free expression.
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 6, lineHeight: 1.8, fontWeight: 600, fontStyle: 'italic' }}
          >
            The café is not just a place to drink coffee. It is a place where ideas are brewed.
          </Typography>

          {/* Section 5 */}
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}
          >
            A Cup of Coffee and a Free Mind
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            The next time you sit with a cup of coffee, it is worth remembering that this simple drink
            once helped create rooms powerful enough to worry kings and sultans.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, fontStyle: 'italic' }}>
            Not because coffee was magic. Because conversation is.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            A good café gives people permission to linger. To question. To laugh. To complain. To
            argue. To listen. To change their minds. To say something imperfect and hear something
            unexpected in return.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            That is the spirit behind{' '}
            <Link
              href="https://freespeechatlas.com/"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontWeight: 'bold' }}
            >
              Free Speech Atlas
            </Link>
            , a site dedicated to exploring the history, controversies, and future of free expression —
            from the First Amendment to social media, censorship, and AI-era speech.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            If coffeehouses were the old gathering places for public debate, today's digital spaces are
            the new ones. The same question follows us from the wooden café table to the glowing screen:
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 6, lineHeight: 1.8, fontWeight: 600, fontStyle: 'italic' }}
          >
            Who gets to speak, and who gets to decide?
          </Typography>

          <Typography variant="body1" sx={{ mb: 6, lineHeight: 1.8 }}>
            <Link
              onClick={() => navigate('/drinks')}
              underline="hover"
              sx={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '1.1rem' }}
            >
              Explore Coffee Cocktails →
            </Link>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default Coffee;
